/**
 * Date / Time Helpers
 *
 * Pure stateless date formatting utilities. No store access.
 *
 * Migrated from: src/mixins/global.js
 */
import moment from 'moment'

/**
 * Formats a raw timestamp string into a human-readable date/time string.
 *
 * @param input     - Raw datetime string (e.g. "2024-01-15 10:30:00.123456")
 * @param format    - 'customReadable' | 'YMDHS' | (anything else for DMY)
 * @param readableTimeFormat - When true, returns a relative "time ago" string
 */
export function renderDateTime_lastReplyTimestamp(
  input: string | undefined | null,
  format: string,
  readableTimeFormat?: boolean
): string | undefined {
  if (!input) return undefined

  let normalized = input.replace(' ', 'T').split('.')[0] + '+00:00'

  const inputDate = normalized.split('T')[0]

  const now = new Date()
  const currentMonth = String(now.getMonth() + 1).padStart(2, '0')
  const currentDate = String(now.getDate()).padStart(2, '0')
  const currentYear = now.getFullYear()
  const currentFullDate = `${currentYear}-${currentMonth}-${currentDate}`

  const isSameDay = moment(inputDate).isSame(currentFullDate)
  const isSameYear = moment(inputDate).isSame(currentFullDate, 'year')

  if (format === 'customReadable') {
    if (isSameDay) return moment(normalized).format('LT')
    if (isSameYear) return moment(normalized).format('MMM DD, LT')
    return moment(normalized).format('MMM DD, YYYY LT')
  }

  const formatted = moment(normalized).format()
  const [datePart, timePart] = formatted.split('T')
  const dateFull = datePart.split('-')

  let date: string
  if (format === 'YMDHS') {
    date = `${dateFull[0]}-${dateFull[1]}-${dateFull[2]}`
  } else {
    date = `${dateFull[2]}-${dateFull[1]}-${dateFull[0]}`
  }

  const timeParts = timePart.split(':')
  const h = timeParts[0]
  const m = timeParts[1]
  const sFull = timeParts[2].indexOf('+') > -1 ? timeParts[2].split('+')[0] : timeParts[2].split('-')[0]

  if (readableTimeFormat) {
    return moment(`${dateFull[0]}${dateFull[1]}${dateFull[2]}${h}${m}`, 'YYYYMMDDHHmm').fromNow()
  }

  return `${date} ${h}:${m}:${sFull}`
}

/**
 * Returns the current UTC datetime as a string suitable for API call timestamps.
 * Format: "YYYY-MM-DD HH:MM:SS.mmm"
 */
export function getAPICallTime(): string {
  return new Date().toISOString().replace('T', ' ').replace('Z', '')
}
